import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWG332Query } from './hooks/usePWG332Query';
import type { PaymentDetail } from '../../services/pwg332Api';
import './PWG332Form.css';

const TYPE_LABEL_MAP: Record<string, string> = {
  A: '繳款',
  C: '折讓',
  D: '客訴',
  E: '調整',
};

const PWG332Form: React.FC = () => {
  const { loading, data, totalAmt, queried, noData, runQuery, reset } = usePWG332Query();
  const [deliveryNo, setDeliveryNo] = useState('');

  const handleQuery = () => {
    runQuery({ deliveryNo: deliveryNo.trim() });
  };

  const handleReset = () => {
    setDeliveryNo('');
    reset();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleQuery();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>交運單繳款明細表</title>
<style>
body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}
h2{text-align:center}
table{width:100%;border-collapse:collapse}
th,td{border:1px solid #333;padding:3px 6px}
th{background:#f0f0f0;text-align:center}
.right{text-align:right}
.unapproved{color:red;font-weight:bold}
tfoot td{font-weight:bold;background:#fafafa}
@media print{body{margin:10mm}}
</style></head><body>
<h2>交運單繳款明細表</h2>
<p>交運單號：${deliveryNo}</p>
<table>
<thead>
<tr>
<th>繳款單號</th>
<th>來源單號</th>
<th>日期</th>
<th>類型</th>
<th>金額</th>
<th>餘額</th>
<th>狀態</th>
<th>核准</th>
</tr>
</thead>
<tbody>
${data.map(r => `<tr>
<td>${r.delivery_no}</td>
<td>${r.doc_no}</td>
<td>${r.doc_date ? r.doc_date.substring(0, 10) : ''}</td>
<td>${r.type_label || TYPE_LABEL_MAP[r.type_code] || r.type_code}</td>
<td class="right">${r.amount.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}</td>
<td class="right">${r.running_bal.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}</td>
<td>${r.status}</td>
<td class="${r.unapproved_mk === '*' ? 'unapproved' : ''}">${r.unapproved_mk || ''}</td>
</tr>`).join('')}
</tbody>
<tfoot>
<tr>
<td colspan="4" style="text-align:right">應繳總金額</td>
<td class="right">${totalAmt.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}</td>
<td colspan="3"></td>
</tr>
</tfoot>
</table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<PaymentDetail> = [
    { title: '繳款單號', dataIndex: 'delivery_no', key: 'delivery_no', width: 110 },
    { title: '來源單號', dataIndex: 'doc_no', key: 'doc_no', width: 110 },
    {
      title: '日期',
      dataIndex: 'doc_date',
      key: 'doc_date',
      width: 110,
      render: (v: string) => v ? v.substring(0, 10) : '',
    },
    {
      title: '類型',
      dataIndex: 'type_code',
      key: 'type_code',
      width: 80,
      render: (_: string, row: PaymentDetail) => row.type_label || TYPE_LABEL_MAP[row.type_code] || row.type_code,
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (v: number) => v.toLocaleString('zh-TW', { minimumFractionDigits: 2 }),
    },
    {
      title: '餘額',
      dataIndex: 'running_bal',
      key: 'running_bal',
      width: 120,
      align: 'right' as const,
      render: (v: number) => v.toLocaleString('zh-TW', { minimumFractionDigits: 2 }),
    },
    { title: '狀態', dataIndex: 'status', key: 'status', width: 80 },
    {
      title: '核准',
      dataIndex: 'unapproved_mk',
      key: 'unapproved_mk',
      width: 60,
      align: 'center' as const,
      render: (v: string) => v === '*' ? <span className="pwg332-unapproved">*</span> : null,
    },
  ];

  const summaryRow = () => {
    if (!queried || noData || data.length === 0) return null;
    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={4} align="right">
          <strong>應繳總金額</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          <strong>{totalAmt.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} colSpan={3} />
      </Table.Summary.Row>
    );
  };

  return (
    <PermissionGuard module={Modules.PWG332} action="query">
      <div className="pwg332-container">
        <div className="pwg332-header">
          <h2>交運單繳款明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
                交運單號
              </label>
              <Input
                value={deliveryNo}
                onChange={e => setDeliveryNo(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="XXXXXX-X"
                style={{ width: 160 }}
                maxLength={8}
                allowClear
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleQuery}
                  loading={loading}
                >
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>
                  清除
                </Button>
              </Space>
            </div>
          </Space>
        </Card>

        {queried && noData && (
          <Empty description="查無此資料" style={{ marginTop: 40 }} />
        )}

        {queried && !noData && (
          <Table<PaymentDetail>
            columns={columns}
            dataSource={data}
            rowKey={(r, idx) => `${r.delivery_no}-${r.doc_no}-${r.type_code}-${idx}`}
            loading={loading}
            size="small"
            pagination={false}
            scroll={{ x: 800 }}
            summary={summaryRow}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWG332Form;
